import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Eye, EyeOff, Loader2, UserPlus, User, Mail, Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatError(detail) {
  if (!detail) return "Ocorreu um erro. Tente novamente.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function RegisterPage() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", confirmPassword: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const setField = (f, v) => { setForm(p => ({ ...p, [f]: v })); setError(""); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.password) {
      setError("Preencha todos os campos obrigatórios.");
      return;
    }
    if (form.password.length < 6) {
      setError("A senha deve ter pelo menos 6 caracteres.");
      return;
    }
    if (form.password !== form.confirmPassword) {
      setError("As senhas não conferem.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const resp = await axios.post(
        `${API}/auth/register`,
        { name: form.name.trim(), email: form.email.trim().toLowerCase(), password: form.password },
        { withCredentials: true }
      );
      setUser(resp.data.user);
      navigate("/profile-setup", { replace: true });
    } catch (e) {
      setError(formatError(e.response?.data?.detail) || "Falha ao criar conta.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      {/* Left panel */}
      <div className="hidden md:flex md:w-1/2 relative overflow-hidden">
        <img
          src="https://images.pexels.com/photos/5207090/pexels-photo-5207090.jpeg"
          alt="Equipe de saúde"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-primary/80 to-primary/40" />
        <div className="absolute inset-0 flex flex-col justify-end p-12 text-white">
          <h2 className="text-3xl font-bold mb-3" style={{ fontFamily: "Manrope" }}>
            Junte-se à RedeCicatriza
          </h2>
          <p className="text-white/80 text-lg">
            Crie sua conta em minutos e comece a acompanhar seus pacientes com mais eficiência.
          </p>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex-1 flex flex-col items-center justify-center p-8 bg-background min-h-screen md:min-h-0">
        <div className="w-full max-w-sm animate-fade-in">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
              <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <div>
              <h1 className="text-xl font-bold text-foreground leading-tight" style={{ fontFamily: "Manrope" }}>
                RedeCicatriza
              </h1>
              <p className="text-xs text-muted-foreground">Atenção Primária</p>
            </div>
          </div>

          <h2 className="text-2xl font-bold text-foreground mb-1" style={{ fontFamily: "Manrope" }}>
            Criar conta
          </h2>
          <p className="text-muted-foreground mb-6 text-sm">
            Após o cadastro, complete seu perfil e aguarde a aprovação do gestor municipal.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Nome completo *</label>
              <div className="relative mt-1">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="text"
                  value={form.name}
                  onChange={e => setField("name", e.target.value)}
                  placeholder="Seu nome completo"
                  autoComplete="name"
                  className="w-full h-12 border border-border rounded-lg pl-10 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  data-testid="name-input"
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Email *</label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="email"
                  value={form.email}
                  onChange={e => setField("email", e.target.value)}
                  placeholder="seu@email.com"
                  autoComplete="email"
                  className="w-full h-12 border border-border rounded-lg pl-10 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  data-testid="register-email-input"
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Senha *</label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type={showPassword ? "text" : "password"}
                  value={form.password}
                  onChange={e => setField("password", e.target.value)}
                  placeholder="Mínimo 6 caracteres"
                  autoComplete="new-password"
                  className="w-full h-12 border border-border rounded-lg pl-10 pr-11 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Confirmar senha *</label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type={showPassword ? "text" : "password"}
                  value={form.confirmPassword}
                  onChange={e => setField("confirmPassword", e.target.value)}
                  placeholder="Repita a senha"
                  autoComplete="new-password"
                  className="w-full h-12 border border-border rounded-lg pl-10 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  data-testid="confirm-password-input"
                />
              </div>
            </div>

            {error && (
              <p className="text-sm text-destructive bg-destructive/10 rounded-lg px-3 py-2" data-testid="register-error">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 py-3.5 bg-primary text-primary-foreground rounded-xl text-sm font-semibold hover:bg-primary/90 transition-all disabled:opacity-60"
              data-testid="register-submit-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
              {loading ? "Criando conta..." : "Criar conta"}
            </button>
          </form>

          <p className="text-center text-sm text-muted-foreground mt-5">
            Já tem conta?{" "}
            <Link to="/login" className="text-primary font-medium hover:underline" data-testid="back-to-login-link">
              Fazer login
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
